import React from 'react';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  query,
  setDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import firebaseAuth, { app, functions } from 'lib/firebase';
import {
  buildInstructorAccountCreationActivity,
  buildInstructorLoginActivity,
  buildMigratedInstructorProfileData,
  canMigrateAssignedInstructorClasses,
  buildInstructorPortalAccountLinkUpdate,
  buildNewInstructorProfileDefaults,
  normalizeInstructorEmail,
} from 'utils/instructorAuthAccount';
import {
  getInstructorStripeSetupLink,
  getInstructorStripeStatus,
  isInstructorStripeComplete,
} from 'utils/instructorStripeStatus';

export const AuthContext = React.createContext(null);

const provider = new GoogleAuthProvider();
const db = getFirestore(app);

const getFriendlyErrorMessage = (errorCode) => {
  const errorMessages = {
    'auth/invalid-email': 'The email address is not valid.',
    'auth/user-disabled': 'The user account has been disabled.',
    'auth/user-not-found': 'No user found with this email.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/too-many-requests': 'Too many attempts. Please try again later.',
    'auth/network-request-failed': 'Network error. Please check your connection.',
    'auth/email-already-in-use': 'This email is already in use.',
    'auth/weak-password': 'The password is too weak.',
    'auth/invalid-login-credentials': 'The email and password combination you entered is incorrect. Please try again.',
  };

  return errorMessages[errorCode] || 'An unknown error occurred. Please try again.';
};

const getInstructorUserDocByEmail = async (email, authUid = '') => {
  const normalizedEmail = normalizeInstructorEmail(email);
  const usersRef = collection(db, 'Users');

  const emailSnapshot = await getDocs(query(
    usersRef,
    where('Email', '==', normalizedEmail),
    limit(10)
  ));
  if (emailSnapshot.empty) return null;

  const authMatchedDoc = emailSnapshot.docs.find((userDoc) => {
    const docData = userDoc.data();
    return authUid && docData.FirebaseAuthUID === authUid;
  });

  if (authMatchedDoc) return authMatchedDoc;

  const unmigratedDoc = emailSnapshot.docs.find((userDoc) => {
    const docData = userDoc.data();
    return !docData.FirebaseAuthUID;
  });

  return unmigratedDoc || emailSnapshot.docs[0];
};

const assertInstructorProfile = (data) => {
  if (data?.Role && data.Role !== 'Instructor') {
    throw new Error('Only instructors can sign in to this portal.');
  }
};

const logInstructorAuthActivity = async (userDocId, activity) => {
  if (!userDocId || !activity) return;
  await addDoc(collection(db, 'Users', userDocId, 'Activity'), activity);
};

const buildAssignedInstructorMigrationActivity = ({
  authUser,
  oldInstructorId,
  newInstructorId,
} = {}) => ({
  type: 'instructor_portal_account_migration',
  title: 'Assigned Instructor Updated',
  body: `AssignedInstructorID migrated from ${oldInstructorId} to ${newInstructorId} during instructor portal account creation.`,
  timestamp: Date.now(),
  actor: {
    uid: authUser?.uid || newInstructorId,
    email: normalizeInstructorEmail(authUser?.email),
  },
  metadata: {
    oldInstructorId,
    newInstructorId,
  },
});

const migrateAssignedInstructorClasses = async ({
  oldInstructorId,
  newInstructorId,
  authUser,
} = {}) => {
  if (!canMigrateAssignedInstructorClasses({ oldInstructorId, newInstructorId })) {
    return 0;
  }

  const classesSnapshot = await getDocs(query(
    collection(db, 'Classes'),
    where('AssignedInstructorID', '==', oldInstructorId)
  ));

  if (classesSnapshot.empty) return 0;

  let batch = writeBatch(db);
  let pendingWrites = 0;
  let migratedClassCount = 0;

  const commitBatch = async () => {
    if (pendingWrites === 0) return;
    await batch.commit();
    batch = writeBatch(db);
    pendingWrites = 0;
  };

  for (const classDoc of classesSnapshot.docs) {
    batch.update(classDoc.ref, { AssignedInstructorID: newInstructorId });
    batch.set(
      doc(collection(db, 'Classes', classDoc.id, 'Activity')),
      buildAssignedInstructorMigrationActivity({
        authUser,
        oldInstructorId,
        newInstructorId,
      })
    );
    pendingWrites += 2;
    migratedClassCount += 1;

    if (pendingWrites >= 400) {
      await commitBatch();
    }
  }

  await commitBatch();
  return migratedClassCount;
};

const syncInstructorPortalAuthFields = async (authUser, resolvedUserDoc) => {
  if (!authUser?.email || !resolvedUserDoc?.userDocId) return resolvedUserDoc;

  const update = buildInstructorPortalAccountLinkUpdate({
    authUser,
    existingUserData: resolvedUserDoc.userData || {},
  });
  await setDoc(doc(db, 'Users', resolvedUserDoc.userDocId), update, { merge: true });

  return {
    ...resolvedUserDoc,
    userData: {
      ...resolvedUserDoc.userData,
      ...update,
    },
  };
};

const ensureInstructorUserDoc = async (user) => {
  if (!user?.email) return { userDocId: null, userData: null };

  const existingDoc = await getInstructorUserDocByEmail(user.email, user.uid);
  if (existingDoc) {
    const data = existingDoc.data();
    assertInstructorProfile(data);
    return { userDocId: existingDoc.id, userData: data };
  }

  const userRef = doc(db, 'Users', user.uid);
  const directDoc = await getDoc(userRef);
  if (directDoc.exists()) {
    const data = directDoc.data();
    assertInstructorProfile(data);
    return { userDocId: directDoc.id, userData: data };
  }

  const nextData = buildNewInstructorProfileDefaults({ email: user.email });
  await setDoc(userRef, nextData, { merge: true });

  return { userDocId: user.uid, userData: nextData };
};

export const ContextProvider = (props) => {
  const [isSignedIn, setIsSignedIn] = React.useState(false);
  const [user, setUser] = React.useState(null);
  const [userDocId, setUserDocId] = React.useState(null);
  const [userData, setUserData] = React.useState(null);
  const [authLoading, setAuthLoading] = React.useState(true);
  const [authError, setAuthError] = React.useState('');
  const [stripeStatus, setStripeStatus] = React.useState(null);
  const [stripeSetupLoading, setStripeSetupLoading] = React.useState(false);
  const [stripeSetupLinkLoading, setStripeSetupLinkLoading] = React.useState(false);
  const stripeSetupPollRef = React.useRef(null);

  const refreshUserData = React.useCallback(async () => {
    if (!firebaseAuth.currentUser) return null;
    const next = await ensureInstructorUserDoc(firebaseAuth.currentUser);
    setUserDocId(next.userDocId);
    setUserData(next.userData);
    return next.userData;
  }, []);

  const refreshStripeSetupStatus = React.useCallback(async (overrides = {}) => {
    const nextUserDocId = overrides.userDocId || userDocId;
    const nextStripeToken = overrides.stripeToken ?? userData?.stripeToken;

    if (!nextUserDocId) {
      setStripeStatus(null);
      setStripeSetupLoading(false);
      return null;
    }

    setStripeSetupLoading(true);
    try {
      const status = await getInstructorStripeStatus({
        functions,
        httpsCallable,
        instructorId: nextUserDocId,
        stripeToken: nextStripeToken,
      });
      setStripeStatus(status);
      return status;
    } catch (error) {
      console.error('Error refreshing Stripe setup status:', error);
      setStripeStatus(null);
      return null;
    } finally {
      setStripeSetupLoading(false);
    }
  }, [userData?.stripeToken, userDocId]);

  const refreshUserAndStripeSetup = React.useCallback(async () => {
    const nextUserData = await refreshUserData();
    await refreshStripeSetupStatus({
      stripeToken: nextUserData?.stripeToken,
    });
  }, [refreshStripeSetupStatus, refreshUserData]);

  const openStripeSetup = React.useCallback(async () => {
    if (!userDocId) throw new Error('Instructor ID is required');

    setStripeSetupLinkLoading(true);
    try {
      const url = await getInstructorStripeSetupLink({
        functions,
        httpsCallable,
        instructorId: userDocId,
        stripeToken: userData?.stripeToken,
        email: userData?.Email || user?.email,
      });
      if (!url) throw new Error('Stripe did not return a setup link.');

      const setupWindow = window.open(url, '_blank', 'noopener,noreferrer');

      if (stripeSetupPollRef.current) {
        window.clearInterval(stripeSetupPollRef.current);
      }

      if (setupWindow) {
        stripeSetupPollRef.current = window.setInterval(() => {
          if (setupWindow.closed) {
            window.clearInterval(stripeSetupPollRef.current);
            stripeSetupPollRef.current = null;
            refreshUserAndStripeSetup();
          }
        }, 1500);
      }

      return url;
    } finally {
      setStripeSetupLinkLoading(false);
    }
  }, [refreshUserAndStripeSetup, user?.email, userData?.Email, userData?.stripeToken, userDocId]);

  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, async (nextUser) => {
      setAuthLoading(true);
      setAuthError('');

      if (!nextUser) {
        setIsSignedIn(false);
        setUser(null);
        setUserDocId(null);
        setUserData(null);
        setStripeStatus(null);
        setAuthLoading(false);
        return;
      }

      try {
        const next = await ensureInstructorUserDoc(nextUser);
        setIsSignedIn(true);
        setUser(nextUser);
        setUserDocId(next.userDocId);
        setUserData(next.userData);
      } catch (error) {
        setIsSignedIn(false);
        setUser(null);
        setUserDocId(null);
        setUserData(null);
        setStripeStatus(null);
        setAuthError(error.message);
        await firebaseSignOut(firebaseAuth);
      } finally {
        setAuthLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  React.useEffect(() => {
    refreshStripeSetupStatus();
  }, [refreshStripeSetupStatus]);

  React.useEffect(() => {
    const handleWindowFocus = () => {
      refreshUserAndStripeSetup();
    };

    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      if (stripeSetupPollRef.current) {
        window.clearInterval(stripeSetupPollRef.current);
        stripeSetupPollRef.current = null;
      }
    };
  }, [refreshUserAndStripeSetup]);

  const signIn = async (email, password) => {
    try {
      const credential = await signInWithEmailAndPassword(firebaseAuth, normalizeInstructorEmail(email), password);
      const next = await syncInstructorPortalAuthFields(
        credential.user,
        await ensureInstructorUserDoc(credential.user)
      );
      await logInstructorAuthActivity(next.userDocId, buildInstructorLoginActivity({
        authUser: credential.user,
        userDocId: next.userDocId,
        authProvider: 'password',
      }));
      setIsSignedIn(true);
      setUser(credential.user);
      setUserDocId(next.userDocId);
      setUserData(next.userData);
      setAuthError('');
      return undefined;
    } catch (err) {
      return err.code ? getFriendlyErrorMessage(err.code) : err.message;
    }
  };

  const sendMagicLink = async (email) => {
    try {
      const normalizedEmail = normalizeInstructorEmail(email);
      if (!normalizedEmail) return 'Please enter your email address.';

      const actionCodeSettings = {
        url: `${window.location.origin}/#/auth/complete-signin`,
        handleCodeInApp: true,
      };

      await sendSignInLinkToEmail(firebaseAuth, normalizedEmail, actionCodeSettings);
      window.localStorage.setItem('instructorPortalMagicEmail', normalizedEmail);
      return undefined;
    } catch (err) {
      return err.code ? getFriendlyErrorMessage(err.code) : err.message;
    }
  };

  const completeMagicLinkSignIn = async (urlString, emailOverride) => {
    try {
      const href = urlString || window.location.href;
      if (!isSignInWithEmailLink(firebaseAuth, href)) {
        return 'This sign-in link is invalid or has expired.';
      }

      const normalizedEmail = normalizeInstructorEmail(
        emailOverride || window.localStorage.getItem('instructorPortalMagicEmail')
      );
      if (!normalizedEmail) {
        return 'Please enter your email address to complete sign in.';
      }

      const credential = await signInWithEmailLink(firebaseAuth, normalizedEmail, href);
      const next = await syncInstructorPortalAuthFields(
        credential.user,
        await ensureInstructorUserDoc(credential.user)
      );
      await logInstructorAuthActivity(next.userDocId, buildInstructorLoginActivity({
        authUser: credential.user,
        userDocId: next.userDocId,
        authProvider: 'magic_link',
      }));
      window.localStorage.removeItem('instructorPortalMagicEmail');
      return undefined;
    } catch (err) {
      return err.code ? getFriendlyErrorMessage(err.code) : err.message;
    }
  };

  const signUp = async (email, password) => {
    try {
      const normalizedEmail = normalizeInstructorEmail(email);
      const existingDoc = await getInstructorUserDocByEmail(normalizedEmail);
      const existingData = existingDoc?.data?.();

      if (existingDoc) {
        assertInstructorProfile(existingData);
      }

      if (existingData?.FirebaseAuthUID) {
        return 'An instructor profile already has portal access. Please sign in instead.';
      }

      const credential = await createUserWithEmailAndPassword(firebaseAuth, normalizedEmail, password);
      let next;

      if (existingDoc) {
        const nowMs = Date.now();
        const migratedProfile = buildMigratedInstructorProfileData({
          authUser: credential.user,
          oldInstructorId: existingDoc.id,
          existingUserData: existingData,
          nowMs,
        });
        const migratedClassCount = await migrateAssignedInstructorClasses({
          oldInstructorId: existingDoc.id,
          newInstructorId: credential.user.uid,
          authUser: credential.user,
        });
        await setDoc(doc(db, 'Users', credential.user.uid), {
          ...migratedProfile,
          PortalMigratedClassCount: migratedClassCount,
        }, { merge: true });
        next = {
          userDocId: credential.user.uid,
          userData: {
            ...migratedProfile,
            PortalMigratedClassCount: migratedClassCount,
          },
        };
      } else {
        next = await ensureInstructorUserDoc(credential.user);
      }

      await logInstructorAuthActivity(next.userDocId, buildInstructorAccountCreationActivity({
        authUser: credential.user,
        userDocId: next.userDocId,
      }));
      await logInstructorAuthActivity(next.userDocId, buildInstructorLoginActivity({
        authUser: credential.user,
        userDocId: next.userDocId,
        authProvider: 'password',
      }));
      setIsSignedIn(true);
      setUser(credential.user);
      setUserDocId(next.userDocId);
      setUserData(next.userData);
      setAuthError('');
      return undefined;
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        return 'An instructor profile already has portal access. Please sign in instead.';
      }
      return err.code ? getFriendlyErrorMessage(err.code) : err.message;
    }
  };

  const signOut = async () => {
    await firebaseSignOut(firebaseAuth);
  };

  const forgotPassword = async (email) => {
    try {
      await sendPasswordResetEmail(firebaseAuth, normalizeInstructorEmail(email));
      return undefined;
    } catch (err) {
      return getFriendlyErrorMessage(err.code);
    }
  };

  const googleSignIn = async () => {
    try {
      const credential = await signInWithPopup(firebaseAuth, provider);
      const next = await syncInstructorPortalAuthFields(
        credential.user,
        await ensureInstructorUserDoc(credential.user)
      );
      await logInstructorAuthActivity(next.userDocId, buildInstructorLoginActivity({
        authUser: credential.user,
        userDocId: next.userDocId,
        authProvider: 'google',
      }));
      setIsSignedIn(true);
      setUser(credential.user);
      setUserDocId(next.userDocId);
      setUserData(next.userData);
      setAuthError('');
      return undefined;
    } catch (err) {
      return err.code ? getFriendlyErrorMessage(err.code) : err.message;
    }
  };

  const stripeSetupComplete = isInstructorStripeComplete(stripeStatus);
  const stripeSetupRequired = Boolean(userDocId && !stripeSetupLoading && !stripeSetupComplete);

  return (
    <AuthContext.Provider
      value={{
        isSignedIn,
        user,
        userDocId,
        userData,
        authLoading,
        authError,
        stripeStatus,
        stripeSetupComplete,
        stripeSetupRequired,
        stripeSetupLoading,
        stripeSetupLinkLoading,
        signIn,
        signUp,
        signOut,
        googleSignIn,
        forgotPassword,
        sendMagicLink,
        completeMagicLinkSignIn,
        refreshUserData,
        refreshStripeSetupStatus,
        openStripeSetup,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};
